import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { reopenSettings } from "../lib/cookieConsent";
import { Wordmark } from "./Wordmark";
import { EntityBlock } from "./EntityBlock";
import { StartAgain } from "./StartAgain";
import { SocialRow } from "./Social";
import { FOOTER_COLS as COLS } from "../lib/navigation";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.email_sent ? "You are on the list. Check your inbox." : "You are on the list.");
      setEmail("");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative border-t border-zinc-900 bg-black" data-testid="site-footer">
      <div className="mx-auto max-w-[1500px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-14 lg:grid-cols-[1.3fr_2fr]">
          <div>
            <Wordmark size="lg" testId="footer-wordmark" />
            <p className="mt-6 max-w-xs text-sm text-zinc-500">
              News, football development progress, training and jobs in Kenyan football.
            </p>
            <div className="mt-8">
              <SocialRow testId="footer-social" label="Follow us" />
            </div>
            <p className="overline mt-8">Newsletter</p>            <h2 className="display-3 mt-4 max-w-md">Follow the progress</h2>
            <p className="body-copy mt-4 max-w-md">
              Transfers, football development updates and Hall of Fame updates. Sent when there is something real to say.
            </p>
            <form onSubmit={subscribe} className="mt-7 flex max-w-md flex-col gap-3 sm:flex-row" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email address"
                data-testid="newsletter-email-input"
                className="field flex-1"
              />
              <button type="submit" disabled={busy} className="btn-primary justify-center disabled:opacity-60" data-testid="newsletter-submit">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />}
                Subscribe
              </button>
            </form>
            <p className="mt-4 max-w-md text-xs leading-relaxed text-zinc-500" data-testid="newsletter-consent">
              By subscribing you agree to receive these updates by email. Unsubscribe from any email. We never
              sell or share your address.{" "}
              <Link to="/privacy" className="text-zinc-300 underline">How we use your data</Link>.
            </p>
          </div>

          <div className="grid gap-10 sm:grid-cols-3">
            {COLS.map((col) => (
              <div key={col.title}>
                <p className="overline">{col.title}</p>
                <ul className="mt-5 space-y-3">
                  {col.links.map(([label, to]) => (
                    <li key={label + to}>
                      <Link
                        to={to}
                        data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-sm text-zinc-400 transition-colors hover:text-ochre"
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 border-t border-zinc-900 pt-10">
          <Wordmark size="footer" testId="footer-wordmark-big" />
        </div>

        <div className="mt-10 border-t border-zinc-900 pt-8" data-testid="footer-estimates">
          <p className="max-w-3xl text-xs leading-relaxed text-zinc-500" data-testid="footer-research-note">
            Everything here is independent football research. Figures are estimates built from published sources
            and are for study and discussion, not professional advice.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <StartAgain label="Clear my selections" testId="footer-start-again" />
            <button type="button" onClick={reopenSettings} data-testid="footer-cookie-settings"
              className="mono border border-zinc-800 px-4 py-2 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-400 transition-colors hover:border-ochre hover:text-ochre">
              Cookie settings
            </button>
          </div>
        </div>

        <div className="mt-10 border-t border-zinc-900 pt-8" data-testid="footer-entity">
          <EntityBlock />
        </div>

        <div className="mt-10 flex flex-col gap-6 border-t border-zinc-900 pt-8 md:flex-row md:items-center md:justify-between">
          <p className="serif text-lg italic text-zinc-500">
            &ldquo;Our Youth. Our Plan. Our Football.&rdquo;
          </p>          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-400">
            <span>© {new Date().getFullYear()} KickStartKenyaFootball</span>
            <span data-testid="footer-credit">Website developed by Proper Pattern</span>
            <Link to="/privacy" className="hover:text-zinc-300">Privacy</Link>
            <Link to="/terms" className="hover:text-zinc-300">Terms</Link>
            <Link to="/sources" className="hover:text-zinc-300">Sources</Link>
            <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-zinc-300">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
